import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Select,
  DatePicker,
  Button,
  Tag,
  Tooltip,
  message,
  Card,
  Avatar,
  Space,
} from "antd";
import {
  SecurityScanOutlined,
  LoginOutlined,
  LogoutOutlined,
  WarningOutlined,
  EnvironmentOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import UserAPI from "../../utils/api/UserAPI";
import {
  BRAND,
  primaryBtn,
  ghostBtn,
  injectButtonHoverStyles,
} from "./buttonStyles";

const { RangePicker } = DatePicker;

const EVENT_TYPES = [
  { value: "", label: "All events" },
  { value: "login", label: "Login" },
  { value: "logout", label: "Logout" },
  { value: "force_signout", label: "Force sign-out" },
  { value: "login_failed", label: "Login failed" },
  { value: "concurrent_block", label: "Concurrent block" },
  { value: "session_expired", label: "Session expired" },
  { value: "login_otp_sent", label: "OTP sent" },
  { value: "login_otp_failed", label: "OTP failed" },
  { value: "account_locked", label: "Account locked" },
  { value: "account_blocked", label: "Account blocked" },
  { value: "account_unblocked", label: "Account restored" },
  { value: "new_ip_login", label: "New IP login" },
];

const STATUSES = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "ended", label: "Ended" },
  { value: "failed", label: "Failed" },
];

const eventMeta = (e) => {
  switch (e) {
    case "login":
      return { color: "green", label: "Login" };
    case "logout":
      return { color: "blue", label: "Logout" };
    case "force_signout":
      return { color: "volcano", label: "Force sign-out" };
    case "login_failed":
      return { color: "red", label: "Login failed" };
    case "concurrent_block":
      return { color: "orange", label: "Concurrent block" };
    case "session_expired":
      return { color: "gold", label: "Session expired" };
    case "login_otp_sent":
      return { color: "cyan", label: "OTP sent" };
    case "login_otp_failed":
      return { color: "red", label: "OTP failed" };
    case "account_locked":
      return { color: "magenta", label: "Account locked" };
    case "account_blocked":
      return { color: "red", label: "Account blocked" };
    case "account_unblocked":
      return { color: "green", label: "Account restored" };
    case "new_ip_login":
      return { color: "purple", label: "New IP login" };
    default:
      return { color: "default", label: e || "-" };
  }
};

const statusColor = (s) => {
  if (s === "active") return "processing";
  if (s === "ended") return "default";
  if (s === "failed") return "error";
  return "default";
};

const formatDuration = (ms) => {
  if (!ms || ms < 0) return "-";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const initials = (name) => {
  if (!name) return "?";
  const parts = String(name).replace(/@.*/, "").split(/[.\s_]+/).filter(Boolean);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
};

const StatCard = ({ icon, label, value, accent }) => (
  <Card
    bordered={false}
    style={{
      borderRadius: 12,
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      borderLeft: `4px solid ${accent}`,
    }}
    bodyStyle={{ padding: "16px 18px" }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 10,
          background: `${accent}14`,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, color: "#1f1f1f" }}>
          {value}
        </div>
        <div style={{ fontSize: 12, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: 0.4 }}>
          {label}
        </div>
      </div>
    </div>
  </Card>
);

const SessionLogsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [username, setUsername] = useState("");
  const [eventType, setEventType] = useState("");
  const [status, setStatus] = useState("");
  const [range, setRange] = useState(null);

  const fetchLogs = async (opts = {}) => {
    setLoading(true);
    try {
      const params = {
        page: opts.page || page,
        limit: opts.limit || limit,
        username: username || undefined,
        event_type: eventType || undefined,
        status: status || undefined,
        from: range && range[0] ? range[0].toISOString() : undefined,
        to: range && range[1] ? range[1].toISOString() : undefined,
      };
      const res = await UserAPI.getSessionLogs(params);
      if (res && res.data && res.data.status) {
        setData(res.data.data || []);
        setTotal(res.data.total || 0);
      } else {
        message.error((res && res.data && res.data.message) || "Failed to load logs");
      }
    } catch (e) {
      message.error("Unable to fetch session logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    injectButtonHoverStyles();
  }, []);

  useEffect(() => {
    fetchLogs({ page, limit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const stats = useMemo(() => {
    const logins = data.filter((d) => d.event_type === "login").length;
    const failureEvents = [
      "login_failed",
      "concurrent_block",
      "login_otp_failed",
      "account_locked",
      "account_blocked",
    ];
    const failures = data.filter((d) => failureEvents.includes(d.event_type)).length;
    const active = data.filter((d) => d.status === "active").length;
    return { logins, failures, active };
  }, [data]);

  const handleApplyFilters = () => {
    setPage(1);
    fetchLogs({ page: 1, limit });
  };

  const handleReset = () => {
    setUsername("");
    setEventType("");
    setStatus("");
    setRange(null);
    setPage(1);
    setTimeout(() => fetchLogs({ page: 1, limit }), 0);
  };

  const handleExport = async () => {
    try {
      const params = {
        username: username || undefined,
        event_type: eventType || undefined,
        status: status || undefined,
        from: range && range[0] ? range[0].toISOString() : undefined,
        to: range && range[1] ? range[1].toISOString() : undefined,
      };
      const res = await UserAPI.exportSessionLogs(params);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `session-logs-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error("Export failed");
    }
  };

  const columns = [
    {
      title: "When",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (v) =>
        v ? (
          <div>
            <div style={{ fontWeight: 500 }}>{new Date(v).toLocaleDateString()}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              {new Date(v).toLocaleTimeString()}
            </div>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "User",
      dataIndex: "username",
      key: "username",
      width: 260,
      render: (v) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar
            size={32}
            style={{
              background: BRAND,
              fontSize: 12,
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            {initials(v)}
          </Avatar>
          <span style={{ fontWeight: 500, wordBreak: "break-all", lineHeight: 1.3 }}>
            {v || "-"}
          </span>
        </div>
      ),
    },
    {
      title: "Event",
      dataIndex: "event_type",
      key: "event_type",
      render: (v) => {
        const m = eventMeta(v);
        return (
          <Tag color={m.color} style={{ borderRadius: 12, padding: "2px 10px" }}>
            {m.label}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => (v ? <Tag color={statusColor(v)}>{v}</Tag> : "-"),
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      render: (v) =>
        v ? <span style={{ fontFamily: "monospace", fontSize: 13 }}>{v}</span> : "-",
    },
    {
      title: "Location",
      key: "geo",
      render: (_, row) => {
        const g = row.geo || {};
        const parts = [g.city, g.region, g.country].filter(Boolean);
        if (!parts.length) return <span style={{ color: "#bfbfbf" }}>Unknown</span>;
        return (
          <Space size={4}>
            <EnvironmentOutlined style={{ color: BRAND }} />
            <span>{parts.join(", ")}</span>
          </Space>
        );
      },
    },
    {
      title: "Duration",
      dataIndex: "duration_ms",
      key: "duration_ms",
      width: 100,
      render: (v) => formatDuration(v),
    },
    {
      title: "Device",
      dataIndex: "user_agent",
      key: "user_agent",
      width: 220,
      ellipsis: { showTitle: false },
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <span style={{ color: "#595959" }}>{v}</span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <div
      style={{
        padding: "24px 28px",
        marginTop: "70px",
        marginLeft: 8,
        background: "#f5f6f8",
        height: "calc(100vh - 70px)",
        overflowY: "auto",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: BRAND,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
            }}
          >
            <SecurityScanOutlined />
          </div>
          <div>
            <h3 style={{ margin: 0, color: BRAND, fontWeight: 700 }}>Session Logs</h3>
            <span style={{ color: "#8c8c8c", fontSize: 13 }}>
              Audit trail of all authentication activity · 90-day retention
            </span>
          </div>
        </div>
        <Space size={10}>
          <Button
            className="sl-btn-ghost"
            icon={<ReloadOutlined />}
            onClick={() => fetchLogs({ page, limit })}
            style={ghostBtn}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            className="sl-btn-primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            style={primaryBtn}
          >
            Export CSV
          </Button>
        </Space>
      </div>

      {/* Stat cards (current page snapshot) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <StatCard icon={<LoginOutlined />} label="Logins (page)" value={stats.logins} accent="#52c41a" />
        <StatCard icon={<WarningOutlined />} label="Failures (page)" value={stats.failures} accent="#ff4d4f" />
        <StatCard icon={<LogoutOutlined />} label="Active (page)" value={stats.active} accent="#1677ff" />
        <StatCard icon={<SecurityScanOutlined />} label="Total records" value={total} accent={BRAND} />
      </div>

      {/* Filters */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        bodyStyle={{ padding: 16 }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            alignItems: "center",
          }}
        >
          <Input
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Search username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onPressEnter={handleApplyFilters}
            allowClear
          />
          <Select
            value={eventType}
            onChange={setEventType}
            options={EVENT_TYPES}
            placeholder="Event"
          />
          <Select value={status} onChange={setStatus} options={STATUSES} placeholder="Status" />
          <RangePicker showTime value={range} onChange={setRange} style={{ width: "100%" }} />
          <Space size={10} style={{ display: "flex" }}>
            <Button
              type="primary"
              className="sl-btn-primary"
              icon={<SearchOutlined />}
              onClick={handleApplyFilters}
              style={{ ...primaryBtn, flex: 1 }}
            >
              Apply
            </Button>
            <Button
              className="sl-btn-ghost"
              onClick={handleReset}
              style={{ ...ghostBtn, flex: 1 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      </Card>

      {/* Table */}
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 8 }}>
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={data}
          columns={columns}
          size="middle"
          scroll={{ x: 1200, y: "calc(100vh - 460px)" }}
          sticky
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            showTotal: (t, r) => (t ? `${r[0]}-${r[1]} of ${t}` : "No records"),
            pageSizeOptions: ["10", "25", "50", "100"],
            onChange: (p, ps) => {
              setPage(p);
              setLimit(ps);
            },
          }}
        />
      </Card>
    </div>
  );
};

export default SessionLogsTable;
